/**
 * "Numbers from your contacts" — what the Mac found in the address book for
 * people in Reppr with an empty phone or email, one tap each.
 *
 * A card matched on a whole name only, and two people can share a name, so
 * nothing is filled in until someone says so. Suggestions for the same empty
 * field sit together: choosing one closes the rest. A number that is already
 * on someone else in Reppr says so before it is chosen.
 *
 * Renders nothing when there is nothing to decide.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { listContactSuggestions, acceptContactSuggestion, skipContactSuggestion, type ContactSuggestionItem } from '../api/contactSuggestions'
import Card, { CardBody } from './Card'
import Button from './Button'
import { showToast } from './Toast'

const FIELD: Record<string, string> = {
  phone: 'phone',
  mobile: 'mobile',
  email: 'email',
  parent_phone: 'parent’s phone',
  parent_email: 'parent’s email',
}

const PATH: Record<string, string> = { client: '/clients', prospect: '/prospects', contact: '/contacts' }

type Group = { key: string; field: string; items: ContactSuggestionItem[] }

function groups(items: ContactSuggestionItem[]): Group[] {
  const map = new Map<string, Group>()
  for (const item of items) {
    const key = `${item.collection}:${item.target_id}:${item.field}`
    const g = map.get(key) ?? { key, field: item.field, items: [] }
    g.items.push(item)
    map.set(key, g)
  }
  return [...map.values()]
}

function whose(item: ContactSuggestionItem): string {
  if (item.person.role === 'parent') return `${item.person.name} (parent)`
  if (item.person.role === 'portal') return `${item.person.name} (portal login)`
  return item.person.name
}

export default function ContactSuggestions() {
  const [items, setItems] = useState<ContactSuggestionItem[] | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    listContactSuggestions()
      .then(rows => { if (!cancelled) setItems(rows) })
      // Silent, like the capture inbox: without a Mac there is nothing here,
      // and the dashboard has more important things to show.
      .catch(() => { if (!cancelled) setItems([]) })
    return () => { cancelled = true }
  }, [])

  if (!items || items.length === 0) return null
  const list = groups(items)

  const act = async (group: Group, remove: string[], run: () => Promise<unknown>, done: string) => {
    setBusy(group.key)
    try {
      await run()
      setItems(prev => (prev ?? []).filter(i => !remove.includes(i.id)))
      showToast(done, 'success')
    } catch (err: any) {
      showToast(err.message, 'error')
      // A field filled in elsewhere meanwhile: the list is out of date.
      listContactSuggestions().then(setItems).catch(() => {})
    } finally {
      setBusy(null)
    }
  }

  return (
    <Card>
      <CardBody>
        <h3 style={{ margin: '0 0 0.25rem', fontSize: '1rem' }}>
          {list.length} missing detail{list.length === 1 ? '' : 's'} found in your contacts
        </h3>
        <p style={{ margin: '0 0 1rem', color: 'var(--muted)', fontSize: '0.875rem' }}>
          Matched by name from the address book on your Mac. Nothing is filled in until you choose.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {list.map(group => {
            const first = group.items[0]
            return (
              <div key={group.key} style={{ borderTop: '1px solid var(--border)', paddingTop: '0.75rem' }}>
                <div style={{ marginBottom: '0.5rem' }}>
                  <Link to={`${PATH[first.person.entity_type]}/${first.person.entity_id}`}>{whose(first)}</Link>
                  <span style={{ color: 'var(--muted)' }}> has no {FIELD[group.field] ?? group.field}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {group.items.map(item => {
                    const others = item.also_on.map(n => n ?? 'an unnamed record')
                    return (
                      <div key={item.id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}>
                        <span>
                          {item.display_value}
                          <span style={{ color: 'var(--muted)', fontSize: '0.8125rem' }}>
                            {item.label ? ` · ${item.label}` : ''} · card “{item.card_name}”
                          </span>
                          {others.length > 0 && (
                            <div style={{ color: 'var(--color-warning-dark)', fontSize: '0.8125rem' }}>Already on {others.join(', ')} in Reppr</div>
                          )}
                        </span>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={busy === group.key}
                          onClick={() => act(group, group.items.map(i => i.id), () => acceptContactSuggestion(item.id), `Saved on ${first.person.name}.`)}
                        >
                          Use this
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={busy === group.key}
                          onClick={() => act(group, [item.id], () => skipContactSuggestion(item.id), 'Skipped.')}
                        >
                          Not theirs
                        </Button>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </CardBody>
    </Card>
  )
}
